import React from 'react';
import { u as useDataListContext } from './DataListContext-CyU4jeBU.js';
import { c as generateHeaderElements, s as sortBreakpoints } from './DataList.utils-CKHBAG6U.js';
import { u as useResponsiveSizing } from './useResponsiveSizing-Di5cJapL.js';
import classnames from 'classnames';
import { A as AnimatedSwitcher } from './AnimatedSwitcher-CFgZoPkz.js';
import { T as Text } from './Text-BCmEgJBp.js';
import { B as Button } from './Button-DJQVVE0-.js';
import { C as Checkbox } from './Checkbox-ugVz15Dc.js';
import { s as styles } from './DataList-DeTUdKxc.js';
import { I as InternalDataListBulkActions } from './DataListBulkActions-CHEE3jkL.js';
import { u as useBatchSelect } from './useBatchSelect-CALxOuDl.js';
import { u as useActiveLayout } from './useActiveLayout-Cit0_Js3.js';

function DataListHeaderCheckbox({ children }) {
    const { sm } = useResponsiveSizing();
    const { data, totalCount } = useDataListContext();
    const { canSelectAll, hasAtLeastOneSelected, isSelectAll, selectedCount, selectedIDs, selected, onSelectAll, onSelect, } = useBatchSelect();
    // If there's no onSelectAll or onSelect, we don't need to render the checkbox.
    if (!canSelectAll)
        return children;
    const deselectText = sm ? "Deselect All" : "Deselect";
    const selectedLabel = selectedCount ? `${selectedCount} selected` : "";
    return (React.createElement("div", { className: styles.selectable },
        React.createElement("div", { className: classnames(styles.selectAllCheckbox, {
                [styles.visible]: canSelectAll,
            }) },
            React.createElement(Checkbox, { checked: isAllSelected(), indeterminate: isIndeterminate(), onChange: handleSelectAll },
                React.createElement("div", { className: styles.srOnly }, selectedLabel))),
        React.createElement(AnimatedSwitcher, { switched: hasAtLeastOneSelected, initialChild: children, switchTo: React.createElement("div", { className: styles.batchSelectContainer },
                React.createElement("div", { className: styles.headerBatchSelect },
                    Boolean(selectedCount) && React.createElement(Text, null,
                        selectedCount,
                        " selected"),
                    React.createElement(Button, { label: deselectText, onClick: () => onSelect === null || onSelect === void 0 ? void 0 : onSelect([]), type: "tertiary" })),
                React.createElement(InternalDataListBulkActions, null)) })));
    function isIndeterminate() {
        if (isSelectAll)
            return selectedIDs.length > 0;
        return selectedCount > 0 && !isAllSelected();
    }
    function isAllSelected() {
        if (isSelectAll)
            return true;
        // If there's a totalCount, we can use that to accurately determine if the
        // user have "selected all".
        if (totalCount) {
            return totalCount === selectedCount;
        }
        // Otherwise, we'll use the total count of data. This is not as reliable, as
        // it's possible that the would select all loaded data while we're
        // loading more. It's still hard to get to that state as the load more
        // triggers before you see the last item.
        return data.length > 0 && selectedCount >= data.length;
    }
    function handleSelectAll() {
        if (isAllSelected()) {
            return onSelectAll === null || onSelectAll === void 0 ? void 0 : onSelectAll([]);
        }
        onSelectAll === null || onSelectAll === void 0 ? void 0 : onSelectAll({ totalCount: getTotalCount(), unselected: [] });
    }
    function getTotalCount() {
        if (selected && "totalCount" in selected)
            return selected.totalCount;
        if (totalCount)
            return totalCount;
        return 0;
    }
}

function DataListHeader() {
    const breakpoints = useResponsiveSizing();
    const { headerVisibility = { xs: true, sm: true, md: true, lg: true, xl: true }, headers, layoutBreakpoints, } = useDataListContext();
    const { hasAtLeastOneSelected } = useBatchSelect();
    const size = getVisibleSize();
    const { layout } = useActiveLayout();
    const visible = headerVisibility[size];
    if ((!visible && !hasAtLeastOneSelected) || !layout)
        return null;
    const headerData = generateHeaderElements(headers);
    if (!headerData)
        return null;
    return (React.createElement("div", { className: styles.headerTitles },
        React.createElement(DataListHeaderCheckbox, null, layout(headerData))));
    function getVisibleSize() {
        const visibilityKeys = Object.keys(headerVisibility);
        const headerBreakpoints = sortBreakpoints(visibilityKeys).reverse();
        const visibleHeaderSize = headerBreakpoints.find(key => breakpoints[key]);
        return visibleHeaderSize || layoutBreakpoints[0];
    }
}

export { DataListHeader as D };
